import { memo } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LoginIcon from "@mui/icons-material/Login";
import ParagraphHighliter from "./paragraphHighliter";
import { resolvePublicMediaUrl } from "../global/functions/imageHelper";
import { useTranslation } from "react-i18next";

const apiUrl = process.env.REACT_APP_MEDIA_URL;

const actionSx = {
  minWidth: 0,
  px: 1,
  py: 0.6,
  fontSize: 12,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: { xs: 0, sm: 0.5 },
  },
};

const RestaurantCard = ({
  data,
  search,
  onOpen,
  onEdit,
  onDelete,
  canManage,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const imageSrc = resolvePublicMediaUrl(data.image || data.logo, apiUrl);
  const types = Array.isArray(data.type) ? data.type.filter((item) => item?.name) : [];

  return (
    <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex" }}>
      <Card
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={onOpen}
          sx={{
            position: "relative",
            height: 180,
            cursor: "pointer",
            bgcolor: "action.hover",
            backgroundImage: imageSrc ? `url(${imageSrc})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "&:hover .open-overlay": { opacity: 1 },
          }}
        >
          {!imageSrc && (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <StorefrontIcon sx={{ fontSize: 48, opacity: 0.5 }} />
            </Box>
          )}
          <Chip
            size="small"
            label={data.active ? t("common.active") : t("common.inactive")}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              fontWeight: 700,
              bgcolor: data.active ? "success.main" : "transparent",
              color: data.active ? "success.contrastText" : "text.secondary",
              border: "1px solid",
              borderColor: data.active ? "success.main" : "divider",
              backdropFilter: "blur(6px)",
            }}
          />
          <Box
            className="open-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0,0,0,0.45)",
              opacity: 0,
              transition: "opacity 0.2s",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            {t("actions.openRestaurant")}
          </Box>
        </Box>

        <CardContent sx={{ flexGrow: 1, px: 2, pt: 2, pb: 1.5 }}>
          <Typography variant="h5" fontWeight={800} sx={{ mb: 1, lineHeight: 1.3 }}>
            <ParagraphHighliter title={data.name} search={search} />
          </Typography>
          {data.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.25,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              <ParagraphHighliter title={data.description} search={search} />
            </Typography>
          ) : null}
          {data.address ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                color: "text.secondary",
                mb: types.length ? 1.25 : 0,
              }}
            >
              <PlaceOutlinedIcon sx={{ fontSize: 18, mt: "1px" }} />
              <Typography variant="body2">{data.address}</Typography>
            </Box>
          ) : null}
          {types.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {types.slice(0, 3).map((type) => (
                <Chip key={type._id || type.name} size="small" label={type.name} variant="outlined" />
              ))}
              {types.length > 3 && (
                <Chip size="small" variant="outlined" label={`+${types.length - 3}`} />
              )}
            </Box>
          )}
        </CardContent>

        <CardActions
          sx={{
            display: "grid",
            gridTemplateColumns: canManage ? "1.2fr 1fr 1fr" : "1fr",
            gap: 0.75,
            px: 2,
            pb: 2,
            pt: 0,
          }}
        >
          <Tooltip title={t("actions.openRestaurant")}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<LoginIcon fontSize="small" />}
              onClick={onOpen}
              sx={actionSx}
            >
              {isNarrow ? null : t("actions.openRestaurant")}
            </Button>
          </Tooltip>
          {canManage && (
            <>
              <Tooltip title={t("common.edit")}>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={onEdit}
                  sx={actionSx}
                >
                  {isNarrow ? null : t("common.edit")}
                </Button>
              </Tooltip>
              <Tooltip title={t("common.delete")}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteForeverIcon fontSize="small" />}
                  onClick={onDelete}
                  sx={actionSx}
                >
                  {isNarrow ? null : t("common.delete")}
                </Button>
              </Tooltip>
            </>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
};

export default memo(RestaurantCard);
